// Copyright 2018-2024 the Deno authors. All rights reserved. MIT license.
// This module is browser compatible.

import { CHAR_COLON } from "../_common/constants.ts";
import { assertPath } from "../_common/assert_path.ts";
import { isPathSeparator, isWindowsDeviceRoot } from "./_util.ts";

/**
 * Verifies whether provided path is absolute.
 *
 * @example Usage
 * ```ts
 * import { isAbsolute } from "@std/path/windows/is-absolute";
 * import { assert, assertFalse } from "@std/assert";
 *
 * assert(isAbsolute("C:\\foo\\bar"));
 * assertFalse(isAbsolute("..\\baz"));
 * ```
 *
 * @param path The path to verify.
 * @returns `true` if the path is absolute, `false` otherwise.
 */
export function isAbsolute(path: string): boolean {
  assertPath(path);

  const len = path.length;
  if (len === 0) return false;

  const code = path.charCodeAt(0);
  if (isPathSeparator(code)) {
    return true;
  } else if (isWindowsDeviceRoot(code)) {
    // Possible device root

    if (len > 2 && path.charCodeAt(1) === CHAR_COLON) {
      if (isPathSeparator(path.charCodeAt(2))) return true;
    }
  }
  return false;
}

// denoCacheMetadata={"headers":{"access-control-allow-origin":"*","x-goog-stored-content-length":"1113","access-control-expose-headers":"*","cross-origin-resource-policy":"cross-origin","x-goog-hash":"crc32c=3XP0mw==,md5=dN810wxRgglY3bzxzxWzZw==","alt-svc":"h3=\":443\"; ma=2592000,h3-29=\":443\"; ma=2592000","date":"Fri, 08 Nov 2024 09:21:07 GMT","age":"2934","x-jsr-cache-status":"hit","content-length":"1113","expires":"Sat, 08 Nov 2025 09:21:07 GMT","last-modified":"Fri, 01 Nov 2024 10:38:02 GMT","content-security-policy":"default-src 'none'; script-src 'none'; style-src 'none'; img-src 'none'; font-src 'none'; connect-src 'none'; frame-src 'none'; object-src 'none'; frame-ancestors 'none'; sandbox; form-action 'none';","via":"1.1 google","server":"UploadServer","x-content-type-options":"nosniff","content-type":"text/typescript","accept-ranges":"bytes","x-goog-metageneration":"1","x-goog-generation":"1730457482806122","x-guploader-uploadid":"AHmUCY1r9GCKyTchK_I6rqWb2SzJKCwz1UEYPDPoOGrxpRE7e_BiHvJW5AR_x4Qhxc5YTm0Eew","x-jsr-cache-id":"IAD","x-robots-tag":"noindex","x-goog-storage-class":"STANDARD","cache-control":"public, max-age=31536000, immutable","x-goog-stored-content-encoding":"identity","etag":"\"74df35d30c51820958ddbcf1cf15b367\""},"url":"https://jsr.io/@std/path/1.0.8/windows/is_absolute.ts","time":1731060601}